import type { Object3D } from 'three';
import type { ClashSource } from './assemblyClash';

// ONE WIREFRAME COLOUR PER PART, AND NEVER THE SAME AS A PART IT TOUCHES.
//
// Wireframes do not hide what is behind them, so two parts in contact drawn in one colour are one
// thicket of lines with no edge between them — which is the one thing the mode is for. Three
// colours handed out in the order the parts were listed did that by arithmetic: a joint of nine
// parts wore each colour three times, and a wood button came out in the rail's purple.
//
// FIXED ONCE, NEVER BY A TOGGLE: a picture of no more parts than colours gives each its own, in
// the order they were given, and no pose can make two of them alike — assembled, exploded, or
// anywhere a movement takes them. Only a bigger picture (the whole product) falls back on the
// parts that touch — their boxes within TOUCH_MM of each other where the caller stands them, the
// first pose it shows, assembled — coloured greedily, the most-touching first, each taking the
// first colour none of its neighbours has. None of them the selection's orange or the collision
// red, which already say something on this picture.
export const WIRE_PALETTE = [
  0x0f766e, // teal
  0x7c3aed, // violet
  0xb45309, // amber
  0x1d4ed8, // blue
  0xbe185d, // pink
  0x4d7c0f, // olive
  0x334155, // slate
  0x0e7490, // cyan
  // TWELVE, so a joint — its parts, its hardware and its covers, nine or ten pieces — has one each.
  0x15803d, // green
  0x78350f, // brown
  0xa21caf, // fuchsia
  0x111827, // near-black
];

const TOUCH_MM = 1;

type Three = typeof import('three');
type Matrix = import('three').Matrix4;

// `place`: where each part stands, as its holder's own matrix (in the holder's parent) — absent,
// where it stands now.
export function wireColours(THREE: Three, bodies: ClashSource, place: (id: string) => Matrix | null): Map<string, number> {
  if (bodies.size <= WIRE_PALETTE.length) return new Map([...bodies.keys()].map((id, k) => [id, WIRE_PALETTE[k]]));
  const boxes = new Map<string, import('three').Box3>();
  for (const [id, { holder, body }] of bodies) {
    holder.updateMatrixWorld(true);
    const inv = new THREE.Matrix4().copy(holder.matrixWorld).invert();
    const local = new THREE.Box3();
    body.traverse((o: Object3D) => {
      const mesh = o as import('three').Mesh;
      if (!mesh.isMesh || !mesh.geometry?.attributes?.position) return;
      mesh.geometry.computeBoundingBox();
      local.union(mesh.geometry.boundingBox!.clone().applyMatrix4(new THREE.Matrix4().copy(inv).multiply(mesh.matrixWorld)));
    });
    if (local.isEmpty()) continue;
    holder.updateMatrix();
    boxes.set(id, local.applyMatrix4(place(id) ?? holder.matrix));
  }
  const ids = [...boxes.keys()];
  const near = new Map(ids.map((id) => [id, new Set<string>()]));
  for (let i = 0; i < ids.length; i += 1) {
    for (let k = i + 1; k < ids.length; k += 1) {
      const a = boxes.get(ids[i])!.clone().expandByScalar(TOUCH_MM / 2);
      if (a.intersectsBox(boxes.get(ids[k])!.clone().expandByScalar(TOUCH_MM / 2))) {
        near.get(ids[i])!.add(ids[k]);
        near.get(ids[k])!.add(ids[i]);
      }
    }
  }
  const out = new Map<string, number>();
  for (const id of [...ids].sort((a, b) => near.get(b)!.size - near.get(a)!.size)) {
    const taken = [...near.get(id)!].flatMap((o) => (out.has(o) ? [out.get(o)!] : []));
    // MORE NEIGHBOURS THAN COLOURS: the one fewest of them wear.
    const free = WIRE_PALETTE.find((c) => !taken.includes(c))
      ?? [...WIRE_PALETTE].sort((a, b) => taken.filter((c) => c === a).length - taken.filter((c) => c === b).length)[0];
    out.set(id, free);
  }
  return out;
}
